import { structureProductArticle } from '../../utils/articleStructure'
import {
  SECTION_UI,
  splitProseAndBullets,
  detectAudienceTags,
  parsePrisleje,
  parseBulletSpecs,
} from '../../utils/articleVisuals'
import { getProductCharacteristics } from '../../utils/productCharacteristics'
import { ArticleMarkdown } from './ArticleMarkdown'
import { ArticleSectionIcon } from './ArticleSectionIcon'
import './ProductArticle.css'

const SECTION_FALLBACK = {
  introduction: 'Introduktion',
  specs: 'Specs',
  audience: 'Målgruppe',
  priceTier: 'Prisleje',
}

const PRISLEJE_CLASS = {
  billigt: 'product-prisleje--billigt',
  gennemsnit: 'product-prisleje--gennemsnit',
  dyrt: 'product-prisleje--dyrt',
}

function prislejeModifier(tier) {
  if (!tier) return ''
  return PRISLEJE_CLASS[tier.toLowerCase()] || ''
}

function getArticleSpecRows(product, article) {
  const fromProduct = getProductCharacteristics(product)
  if (fromProduct.length > 0) return fromProduct
  const { bullets } = splitProseAndBullets(article.specs || '')
  return parseBulletSpecs(bullets)
}

function SpecsTable({ rows }) {
  if (!rows.length) return null
  return (
    <div className="product-article-specs-table-box">
      <h3 className="product-article-specs-table-heading">Specifikationer</h3>
      <table className="product-article-specs-table">
        <tbody>
          {rows.map(({ key, label, value }) => (
            <tr key={key}>
              <th scope="row">{label}</th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AudienceTags({ tags }) {
  if (!tags.length) return null
  return (
    <ul className="product-article-audience-tags" aria-label="Målgruppe og spillestil">
      {tags.map(({ id, label }) => (
        <li key={id}>{label}</li>
      ))}
    </ul>
  )
}

function PrislejePanel({ content }) {
  const { tier, reason } = parsePrisleje(content)
  const mod = prislejeModifier(tier)
  return (
    <div className={`product-article-prisleje-panel ${mod}`.trim()}>
      {tier && (
        <p className="product-article-prisleje-tier">
          <span className="product-article-prisleje-tier-label">{tier}</span>
        </p>
      )}
      {reason && <p className="product-article-prisleje-reason">{reason}</p>}
    </div>
  )
}

function SectionBody({ sectionKey, content }) {
  if (sectionKey === 'priceTier') {
    return <PrislejePanel content={content} />
  }

  if (sectionKey === 'specs') {
    const { prose, bullets } = splitProseAndBullets(content)
    const text = prose || (bullets.length ? '' : content)
    if (!text?.trim()) return null
    return (
      <div className="description-formatted product-article-prose">
        <ArticleMarkdown content={text} />
      </div>
    )
  }

  if (sectionKey === 'audience') {
    const tags = detectAudienceTags(content)
    return (
      <>
        <AudienceTags tags={tags} />
        <div className="description-formatted product-article-prose">
          <ArticleMarkdown content={content} />
        </div>
      </>
    )
  }

  return (
    <div
      className={`description-formatted product-article-prose${
        sectionKey === 'introduction' ? ' product-article-prose--lead' : ''
      }`}
    >
      <ArticleMarkdown content={content} />
    </div>
  )
}

export function ProductArticle({ product }) {
  const article = structureProductArticle({
    description: product.description,
    summary: product.summary,
  })

  if (!article.hasContent) {
    return null
  }

  const specRows = getArticleSpecRows(product, article)
  const hasSidebar = specRows.length > 0

  const blocks =
    article.sections.length > 0
      ? article.sections
      : Object.entries(SECTION_FALLBACK)
          .filter(([key]) => article[key])
          .map(([key, label]) => ({
            key,
            id: `artikel-${key}`,
            label,
            content: article[key],
          }))

  return (
    <div className="product-article">
      <nav className="product-article-nav" aria-label="Artikelindhold">
        {blocks.map(({ key, id, label }) => {
          const ui = SECTION_UI[key] || {}
          return (
            <a key={key} href={`#${id}`} className="product-article-nav-link">
              <span className="product-article-nav-step">{ui.step}</span>
              {label}
            </a>
          )
        })}
      </nav>

      <div
        className={[
          'product-article-layout',
          hasSidebar ? 'product-article-layout--with-sidebar' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {hasSidebar && (
          <aside className="product-article-sidebar scroll-target" id="artikel-specifikationer">
            <SpecsTable rows={specRows} />
          </aside>
        )}

        <div className="product-article-main">
          {blocks.map(({ key, id, label, content }) => {
            const ui = SECTION_UI[key] || {}
            const isPrisleje = key === 'priceTier'
            const tier = isPrisleje ? parsePrisleje(content).tier : null

            return (
              <section
                key={key}
                id={id}
                className={[
                  'product-article-block scroll-target',
                  `product-article-block--${key}`,
                  isPrisleje ? 'product-article-prisleje' : '',
                  isPrisleje ? prislejeModifier(tier) : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ '--section-accent': ui.accent || '#6366f1' }}
              >
                <header className="product-article-block-header">
                  <div
                    className="product-article-block-icon-wrap"
                    style={{ background: `${ui.accent}18`, color: ui.accent }}
                  >
                    <ArticleSectionIcon sectionKey={key} />
                  </div>
                  <div className="product-article-block-titles">
                    <span className="product-article-block-step">
                      {ui.step}. {ui.subtitle}
                    </span>
                    <h2>{label}</h2>
                  </div>
                </header>
                <div className="product-article-block-body">
                  <SectionBody sectionKey={key} content={content} />
                </div>
              </section>
            )
          })}
        </div>
      </div>
    </div>
  )
}
